import React, { useState } from 'react';
import { LuChevronDown, LuFolderPlus, LuPlus, LuWallet } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useHouseholdRouteType, useRequiredParams } from 'utils';
import { CreateAccountModal } from 'views/account/CreateAccountModal';
import { AccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';

import { CreatePortfolioModal } from './CreatePortfolioModal';
import { FlatAccountsTable } from './FlatAccountsTable';
import { HouseholdAssetClassExposureCard } from './HouseholdAssetClassExposureCard';

interface PortfolioListProps {
    accountGroupProfiles: AccountGroupProfileWithAumIncluded[];
}

export const CompactPortfolioList = ({ accountGroupProfiles }: PortfolioListProps) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
    const [showCreatePortfolioModal, setShowCreatePortfolioModal] = useState(false);
    const { householdTypeLabel } = useHouseholdRouteType();
    const hasPortfolios = accountGroupProfiles.length > 0;

    return (
        <>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start">
                <div>
                    {!hasPortfolios ? (
                        <div className="flex h-48 items-center justify-center">
                            <div className="text-center">
                                <p className="mb-2 text-gray-500">No portfolios found for this {householdTypeLabel}.</p>
                            </div>
                        </div>
                    ) : (
                        <FlatAccountsTable accountGroupProfiles={accountGroupProfiles} />
                    )}

                    <div className="mt-4 flex justify-end">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button className="flex items-center gap-1.5 font-medium">
                                    <LuPlus /> Create <LuChevronDown className="h-3.5 w-3.5 opacity-70" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={() => setShowCreatePortfolioModal(true)}
                                    className="flex items-center gap-2"
                                >
                                    <LuFolderPlus className="h-4 w-4" /> New Portfolio
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => setShowCreateAccountModal(true)}
                                    disabled={!hasPortfolios}
                                    title={!hasPortfolios ? 'Create a portfolio first' : undefined}
                                    className="flex items-center gap-2"
                                >
                                    <LuWallet className="h-4 w-4" /> New Account
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {hasPortfolios && <HouseholdAssetClassExposureCard accountGroupProfiles={accountGroupProfiles} />}
            </div>

            {showCreatePortfolioModal && (
                <CreatePortfolioModal
                    householdGUID={globalAccountGUID}
                    onClose={() => setShowCreatePortfolioModal(false)}
                />
            )}

            {showCreateAccountModal && (
                <CreateAccountModal
                    isOpen
                    onClose={() => setShowCreateAccountModal(false)}
                    accountGroups={accountGroupProfiles.map((profile) => profile.accountGroup)}
                />
            )}
        </>
    );
};
